import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import TemplatePicker from './TemplatePicker';
import ImagePickerButton from '../shared/ImagePickerButton';
import { NOTE_IMAGE_MAX_UPLOAD_SIZE, NOTE_IMAGE_ALLOWED_TYPES, NOTE_IMAGE_ACCEPT_STRING } from './imageConstants';
import { useUserStore } from '../../store';

function AddNoteDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [showOnInvoice, setShowOnInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    if (open) {
      setText('');
      setNoteType(currentOrganization?.default_note_type_customer ? 'customer' : 'internal');
      setShowOnInvoice(currentOrganization?.default_note_show_on_invoice ?? false);
      setSelectedImages([]);
      setError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      if (!NOTE_IMAGE_ALLOWED_TYPES.includes(file.type)) {
        setError(t('Invalid file type. Allowed: JPEG, PNG, WebP, GIF'));
        return false;
      }
      if (file.size > NOTE_IMAGE_MAX_UPLOAD_SIZE) {
        setError(t('File too large (max {{maxMB}}MB)', { maxMB: NOTE_IMAGE_MAX_UPLOAD_SIZE / 1024 / 1024 }));
        return false;
      }
      return true;
    });

    const newImages = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Require either text or images
    if (!text.trim() && selectedImages.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('text', text.trim());
      formData.append('note_type', noteType);
      if (noteType === 'customer') {
        formData.append('show_on_invoice', showOnInvoice);
      }

      selectedImages.forEach((img) => {
        formData.append('images', img.file);
      });

      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/add_note/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMaintenance(response.data);

      // Show warnings if any
      if (response.data.image_warnings) {
        console.warn('Image upload warnings:', response.data.image_warnings);
      }

      // Cleanup previews
      selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
      setText('');
      setNoteType(currentOrganization?.default_note_type_customer ? 'customer' : 'internal');
      setShowOnInvoice(currentOrganization?.default_note_show_on_invoice ?? false);
      setSelectedImages([]);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setNoteType(newType);
    }
  };

  const handleCancel = () => {
    // Cleanup previews
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setText('');
    setNoteType(currentOrganization?.default_note_type_customer ? 'customer' : 'internal');
    setShowOnInvoice(currentOrganization?.default_note_show_on_invoice ?? false);
    setSelectedImages([]);
    setError(null);
    handleClose();
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/generate_customer_note/`,
        { language: 'fi' }
      );
      setText(response.data.generated_text);
      setNoteType('customer'); // AI generates customer notes
    } catch (err) {
      setError(err.response?.data?.error || t('Failed to generate note'));
    } finally {
      setGenerating(false);
    }
  };

  const canSubmit = text.trim() || selectedImages.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600,
          pb: 1,
        }}
      >
        {t('Add note')}
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mb: 1.5,
            }}
          >
            {t('Note type')}
          </Typography>
          <ToggleButtonGroup
            value={noteType}
            exclusive
            onChange={handleTypeChange}
            fullWidth
            orientation="horizontal"
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiToggleButton-root': {
                py: 1.5,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                textTransform: 'none',
                '&.Mui-selected': {
                  fontWeight: 600,
                },
              },
              '& .MuiToggleButtonGroup-grouped': {
                '&:not(:first-of-type)': {
                  borderTop: { xs: 0, sm: '1px solid rgba(0, 0, 0, 0.12)' },
                  borderLeft: { xs: '1px solid rgba(0, 0, 0, 0.12)', sm: 0 },
                  borderRadius: { xs: '0 0 8px 8px', sm: '0 8px 8px 0' },
                  marginLeft: { xs: 0, sm: '-1px' },
                  marginTop: { xs: '-1px', sm: 0 },
                },
                '&:first-of-type': {
                  borderRadius: { xs: '8px 8px 0 0', sm: '8px 0 0 8px' },
                },
              },
            }}
          >
            <ToggleButton
              value="internal"
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(100, 116, 139, 0.1)',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'rgba(100, 116, 139, 0.15)',
                  },
                },
              }}
            >
              <LockIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('Internal note')}
            </ToggleButton>
            <ToggleButton
              value="customer"
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'rgba(49, 130, 206, 0.1)',
                  color: 'secondary.main',
                  '&:hover': {
                    bgcolor: 'rgba(49, 130, 206, 0.15)',
                  },
                },
              }}
            >
              <PublicIcon sx={{ mr: 1, fontSize: 20 }} />
              {t('Customer note')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {noteType === 'customer' && (
          <Alert
            severity="info"
            icon={<PublicIcon />}
            sx={{
              mb: 2,
              borderRadius: '8px',
              '& .MuiAlert-message': {
                fontSize: '13px',
              },
            }}
          >
            {t('Customer note warning')}
          </Alert>
        )}

        {noteType === 'internal' && (
          <Alert
            severity="warning"
            icon={<LockIcon />}
            sx={{
              mb: 2,
              borderRadius: '8px',
              bgcolor: 'rgba(100, 116, 139, 0.08)',
              color: 'text.secondary',
              '& .MuiAlert-icon': {
                color: 'text.secondary',
              },
              '& .MuiAlert-message': {
                fontSize: '13px',
              },
            }}
          >
            {t('Internal note info')}
          </Alert>
        )}

        {/* Template Picker & AI Generate - only for customer notes */}
        {noteType === 'customer' && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TemplatePicker
              maintenance={maintenance}
              onSelect={(text) => setText(text)}
            />
            <Button
              variant="outlined"
              startIcon={
                generating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              onClick={handleGenerate}
              disabled={generating}
              size="small"
              sx={{
                borderColor: 'secondary.main',
                color: 'secondary.main',
                borderRadius: '8px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'secondary.dark',
                  bgcolor: 'rgba(49, 130, 206, 0.04)',
                },
              }}
            >
              {generating ? <span>{t('Generating...')}</span> : <span>{t('Generate with AI')}</span>}
            </Button>
          </Box>
        )}

        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          placeholder={
            noteType === 'customer'
              ? t('Write a message for the customer...')
              : t('Write an internal note...')
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        />

        {noteType === 'customer' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnInvoice}
                onChange={(e) => setShowOnInvoice(e.target.checked)}
                size="small"
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('Show on invoice')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('Show on invoice help')}
                </Typography>
              </Box>
            }
            sx={{ mt: 1.5, ml: 0 }}
          />
        )}

        {/* Image Upload Section */}
        <Box sx={{ mt: 2 }}>
          <ImagePickerButton
            onChange={handleImageSelect}
            accept={NOTE_IMAGE_ACCEPT_STRING}
            multiple
            buttonLabel={t('Attach images')}
            buttonProps={{
              variant: 'outlined',
              startIcon: <AttachFileIcon />,
              size: 'small',
              sx: {
                borderRadius: '8px',
                fontWeight: 500,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                textTransform: 'none',
              },
            }}
          />

          {selectedImages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: 'text.secondary',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ImageIcon sx={{ fontSize: 16 }} />
                {selectedImages.length}{' '}
                {selectedImages.length === 1 ? t('image') : t('images')}
              </Typography>
              <ImageList
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
                cols={4}
                rowHeight={80}
                gap={8}
              >
                {selectedImages.map((img, index) => (
                  <ImageListItem
                    key={index}
                    sx={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <img
                      src={img.preview}
                      alt={img.name}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <ImageListItemBar
                      sx={{
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                        '& .MuiImageListItemBar-titleWrap': {
                          padding: '4px',
                        },
                      }}
                      position="bottom"
                      actionIcon={
                        <IconButton
                          size="small"
                          onClick={() => removeImage(index)}
                          sx={{
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      }
                      actionPosition="right"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 1, fontSize: '13px' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          sx={{
            bgcolor: noteType === 'customer' ? 'secondary.main' : 'grey.700',
            borderRadius: '8px',
            px: 2.5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 500,
            '&:hover': {
              bgcolor: noteType === 'customer' ? 'secondary.dark' : 'grey.800',
            },
          }}
        >
          {loading ? <span>{t('Adding...')}</span> : <span>{t('Add note')}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddNoteDialog;
